import React, { useEffect, useMemo, useState } from "react";
import { getStockSnapshot } from "../marketData/marketDataApi";
import {
  addUserWatchlistSymbol,
  getUserWatchlist,
  removeUserWatchlistSymbol,
} from "../marketData/watchlistApi";
import "./NinaMissionPanel.css";

const WATCHLIST_REFRESH_MS = 15000;
const SYMBOL_PATTERN = /^[A-Z][A-Z0-9.-]{0,9}$/;

const SECTION_DETAILS = {
  portfolio: {
    eyebrow: "ACCOUNT PORTFOLIO",
    title: "Portfolio",
    description:
      "Portfolio balances and positions appear here only from an authenticated broker-backed source.",
    badge: "AUTHENTICATED DATA ONLY",
    notice:
      "No sample balances, positions, buying power, or profit/loss values are generated on this page.",
  },
  "nina-status": {
    eyebrow: "AI SYSTEM STATUS",
    title: "AI Status",
    description:
      "System status appears only from connected AninaTrade services and authenticated evidence.",
    badge: "AUTHENTICATED DATA ONLY",
    notice:
      "No synthetic confidence, readiness, scanner counts, or market-bias values are generated.",
  },
  watchlist: {
    eyebrow: "AUTHENTICATED MARKET MONITOR",
    title: "Watchlist",
    description:
      "Build your personal Watchlist and track it with authenticated read-only Alpaca market data.",
    badge: "AUTHENTICATED READ-ONLY",
    notice:
      "MY WATCHLIST · Account-synced symbols · Read-only market data · Authenticated values only",
  },
  "market-clock": {
    eyebrow: "MARKET SESSION STATUS",
    title: "Market Clock",
    description:
      "Session timing appears only from an authenticated market-clock and exchange-calendar source.",
    badge: "AUTHENTICATED DATA ONLY",
    notice:
      "No sample session state or countdown is generated while the authenticated market clock is not connected.",
  },
};

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function firstFinite(...values) {
  for (const value of values) {
    const normalized = finiteNumber(value);
    if (normalized !== null) return normalized;
  }
  return null;
}

function emptyWatchlistRow(symbol) {
  return {
    symbol,
    status: "loading",
    price: null,
    dailyPercent: null,
    provider: "",
    feed: "",
    sourceLabel: "",
    quality: "",
    isFresh: true,
    updatedAt: null,
    error: "",
  };
}

function normalizeWatchlistSnapshot(symbol, payload) {
  const currentPrice = firstFinite(payload?.latest_trade?.p, payload?.minute_bar?.c);
  const previousClose = finiteNumber(payload?.previous_daily_bar?.c);
  const dailyPercent =
    currentPrice !== null && previousClose !== null && previousClose > 0
      ? ((currentPrice - previousClose) / previousClose) * 100
      : null;
  const source = payload?.source || {};
  const provider = String(source.provider || "Authenticated provider").trim();
  const feed = String(source.feed || "").trim().toUpperCase();
  const sourceLabel = String(source.label || "").trim();

  return {
    symbol,
    status: currentPrice === null ? "unavailable" : "ready",
    price: currentPrice,
    dailyPercent,
    provider,
    feed,
    sourceLabel,
    quality: String(source.quality_level || "").trim(),
    isFresh: source.is_fresh !== false,
    updatedAt: source.last_update_at || payload?.latest_trade?.t || null,
    error: currentPrice === null ? "Authenticated price unavailable." : "",
  };
}

function NinaMissionPanel({ section }) {
  const details = SECTION_DETAILS[section];
  if (!details) return null;

  return (
    <section
      className="ninaMissionWorkspace"
      aria-labelledby={`mission-workspace-${section}`}
    >
      <header className="missionWorkspaceHeader">
        <div>
          <span>{details.eyebrow}</span>
          <h1 id={`mission-workspace-${section}`}>{details.title}</h1>
          <p>{details.description}</p>
        </div>
        <strong className="authenticatedBadge">
          {details.badge}
        </strong>
      </header>

      <p
        className="missionWorkspaceNotice authenticatedNotice"
      >
        {details.notice}
      </p>

      {section === "portfolio" && (
        <UnavailableWorkspace
          title="Portfolio source not connected"
          detail="Connect an authenticated broker-backed portfolio source before balances, buying power, positions, or profit/loss are shown here."
        />
      )}
      {section === "nina-status" && (
        <UnavailableWorkspace
          title="Authenticated AI status not connected"
          detail="This page will report only verified AninaTrade service health, scanner activity, and AI evidence when the status source is connected."
        />
      )}
      {section === "watchlist" && <WatchlistWorkspace />}
      {section === "market-clock" && (
        <UnavailableWorkspace
          title="Authenticated market clock not connected"
          detail="This page will show exchange session state and countdowns only after an authenticated market-calendar source is connected."
        />
      )}
    </section>
  );
}

function UnavailableWorkspace({ title, detail }) {
  return (
    <section className="missionUnavailableCard" aria-label={title}>
      <span>AUTHENTICATED SOURCE REQUIRED</span>
      <strong>{title}</strong>
      <p>{detail}</p>
    </section>
  );
}

export function WatchlistWorkspace() {
  const [symbols, setSymbols] = useState([]);
  const [maxSymbols, setMaxSymbols] = useState(50);
  const [membershipStatus, setMembershipStatus] = useState("loading");
  const [membershipError, setMembershipError] = useState("");
  const [rows, setRows] = useState([]);
  const [symbolQuery, setSymbolQuery] = useState("");
  const [mutationError, setMutationError] = useState("");
  const [mutatingSymbol, setMutatingSymbol] = useState("");

  useEffect(() => {
    let active = true;
    setMembershipStatus("loading");
    getUserWatchlist()
      .then((result) => {
        if (!active) return;
        setSymbols(Array.isArray(result?.symbols) ? result.symbols : []);
        setMaxSymbols(Number(result?.max_symbols) || 50);
        setMembershipError("");
        setMembershipStatus("ready");
      })
      .catch((error) => {
        if (!active) return;
        setSymbols([]);
        setMembershipError(error.message || "My Watchlist is unavailable.");
        setMembershipStatus("error");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setRows((current) =>
      symbols.map(
        (symbol) => current.find((row) => row.symbol === symbol) || emptyWatchlistRow(symbol),
      ),
    );
  }, [symbols]);

  useEffect(() => {
    if (membershipStatus !== "ready" || symbols.length === 0) return undefined;

    let active = true;
    let timerId = null;

    const loadQuotes = async ({ preserveCurrent = false } = {}) => {
      if (!preserveCurrent) {
        setRows((current) =>
          current.map((row) => ({ ...row, status: "loading", error: "" })),
        );
      }

      const results = await Promise.allSettled(
        symbols.map((symbol) => getStockSnapshot(symbol)),
      );
      if (!active) return;

      setRows((current) =>
        results.map((result, index) => {
          const symbol = symbols[index];
          if (result.status === "fulfilled") {
            return normalizeWatchlistSnapshot(symbol, result.value);
          }

          const previous = current.find((row) => row.symbol === symbol) || emptyWatchlistRow(symbol);
          return {
            ...previous,
            symbol,
            status: "error",
            price: preserveCurrent ? previous.price ?? null : null,
            dailyPercent: preserveCurrent ? previous.dailyPercent ?? null : null,
            provider: preserveCurrent ? previous.provider || "" : "",
            feed: preserveCurrent ? previous.feed || "" : "",
            sourceLabel: preserveCurrent ? previous.sourceLabel || "" : "",
            quality: preserveCurrent ? previous.quality || "" : "",
            updatedAt: preserveCurrent ? previous.updatedAt || null : null,
            error:
              result.reason?.message ||
              "Authenticated Alpaca market data is unavailable for this symbol.",
          };
        }),
      );
    };

    loadQuotes();
    timerId = window.setInterval(
      () => loadQuotes({ preserveCurrent: true }),
      WATCHLIST_REFRESH_MS,
    );

    return () => {
      active = false;
      if (timerId) window.clearInterval(timerId);
    };
  }, [membershipStatus, symbols]);

  const normalizedQuery = symbolQuery.trim().toUpperCase();
  const visibleRows = useMemo(
    () =>
      normalizedQuery
        ? rows.filter((row) => row.symbol.includes(normalizedQuery))
        : rows,
    [normalizedQuery, rows],
  );

  const loadingCount = rows.filter((row) => row.status === "loading").length;
  const errorCount = rows.filter((row) => row.status === "error").length;
  const latestUpdate = rows
    .map((row) => row.updatedAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  const applyMembership = (result) => {
    setSymbols(Array.isArray(result?.symbols) ? result.symbols : []);
    setMaxSymbols(Number(result?.max_symbols) || maxSymbols);
    setMembershipError("");
  };

  const handleAddSymbol = async (event) => {
    event.preventDefault();
    const symbol = normalizedQuery;
    setMutationError("");
    if (!SYMBOL_PATTERN.test(symbol)) {
      setMutationError("Enter a valid stock symbol, such as AAPL or BRK.B.");
      return;
    }

    setMutatingSymbol(symbol);
    try {
      const result = await addUserWatchlistSymbol(symbol);
      applyMembership(result);
      setSymbolQuery("");
    } catch (error) {
      setMutationError(error.message || "Unable to add that symbol.");
    } finally {
      setMutatingSymbol("");
    }
  };

  const handleRemoveSymbol = async (symbol) => {
    setMutationError("");
    setMutatingSymbol(symbol);
    try {
      const result = await removeUserWatchlistSymbol(symbol);
      applyMembership(result);
    } catch (error) {
      setMutationError(error.message || `Unable to remove ${symbol}.`);
    } finally {
      setMutatingSymbol("");
    }
  };

  return (
    <div className="missionWatchlistCard">
      <div className="myWatchlistToolbar" aria-label="My Watchlist controls">
        <div className="myWatchlistTitle">
          <span>PERSONAL MARKET LIST</span>
          <strong>My Watchlist</strong>
          <small>{symbols.length} / {maxSymbols} symbols</small>
        </div>
        <form className="myWatchlistActions" onSubmit={handleAddSymbol}>
          <label className="myWatchlistSearch">
            <span className="visuallyHidden">Search or add symbol</span>
            <input
              type="search"
              value={symbolQuery}
              onChange={(event) => setSymbolQuery(event.target.value.toUpperCase())}
              placeholder="Search or add symbol"
              aria-label="Search or add symbol"
              maxLength={10}
              autoComplete="off"
            />
          </label>
          <button
            type="submit"
            disabled={Boolean(mutatingSymbol) || membershipStatus !== "ready" || !symbolQuery.trim()}
          >
            {mutatingSymbol ? "Adding…" : "Add"}
          </button>
        </form>
      </div>

      {(mutationError || membershipError) && (
        <p className="myWatchlistError" role="alert">{mutationError || membershipError}</p>
      )}

      <div className="missionWatchlistStatus" role="status">
        <strong>
          {membershipStatus === "loading"
            ? "Loading My Watchlist"
            : loadingCount > 0
              ? "Loading authenticated quotes"
              : "Authenticated quotes loaded"}
        </strong>
        <span>
          {membershipStatus === "error"
            ? "Account Watchlist unavailable · authenticated account data required"
            : errorCount > 0
              ? `${errorCount} symbol${errorCount === 1 ? "" : "s"} unavailable · authenticated data only`
              : "Account-synced · read-only market data · no order capability"}
        </span>
      </div>

      <div className="missionWatchlist" role="table" aria-label="My authenticated watchlist">
        <div className="missionWatchlistHead" role="row">
          <span role="columnheader">Symbol</span>
          <span role="columnheader">Price</span>
          <span role="columnheader">Daily %</span>
          <span role="columnheader">Direction</span>
          <span role="columnheader">Source</span>
          <span role="columnheader">Manage</span>
        </div>
        {visibleRows.map((item) => {
          const hasMove = finiteNumber(item.dailyPercent) !== null;
          const positive = hasMove && item.dailyPercent >= 0;
          const sourceText = item.sourceLabel || [item.provider, item.feed].filter(Boolean).join(" · ");
          const rowLabel = `${item.symbol}, authenticated price ${formatOptionalCurrency(
            item.price,
          )}, daily change ${formatOptionalPercent(item.dailyPercent)}`;

          return (
            <div
              className="missionWatchlistRow"
              role="row"
              key={item.symbol}
              aria-label={rowLabel}
            >
              <strong role="cell">{item.symbol}</strong>
              <span role="cell">{formatOptionalCurrency(item.price)}</span>
              <span
                role="cell"
                className={hasMove ? (positive ? "missionMoveUp" : "missionMoveDown") : ""}
              >
                {formatOptionalPercent(item.dailyPercent)}
              </span>
              <span
                role="cell"
                className={hasMove ? (positive ? "missionMoveUp" : "missionMoveDown") : ""}
                aria-label={hasMove ? (positive ? "Up" : "Down") : "Unavailable"}
              >
                <i aria-hidden="true">{hasMove ? (positive ? "▲" : "▼") : "—"}</i>
              </span>
              <span className="missionWatchlistSource" role="cell" title={item.quality || item.error || sourceText}>
                {item.status === "loading"
                  ? "Loading…"
                  : item.status === "error" && !sourceText
                    ? "Unavailable"
                    : `${sourceText || "Authenticated"}${item.isFresh ? "" : " · stale"}`}
              </span>
              <span className="missionWatchlistManage" role="cell">
                <button
                  type="button"
                  onClick={() => handleRemoveSymbol(item.symbol)}
                  disabled={mutatingSymbol === item.symbol}
                  aria-label={`Remove ${item.symbol} from My Watchlist`}
                >
                  {mutatingSymbol === item.symbol ? "Removing…" : "Remove"}
                </button>
              </span>
            </div>
          );
        })}
      </div>

      {membershipStatus === "ready" && symbols.length === 0 && (
        <div className="myWatchlistEmpty">
          <strong>Your Watchlist is empty.</strong>
          <span>Type a symbol above and select Add to start tracking a company or ETF.</span>
        </div>
      )}
      {membershipStatus === "ready" && symbols.length > 0 && visibleRows.length === 0 && (
        <div className="myWatchlistEmpty">
          <strong>No Watchlist symbols match “{symbolQuery.trim()}”.</strong>
          <span>Press Add to save this symbol, or clear the field to see all {symbols.length} saved symbols.</span>
        </div>
      )}

      <p>
        {latestUpdate
          ? `Latest authenticated update ${formatTimestamp(latestUpdate)}. Membership is saved to your AninaTrade account; prices remain authenticated-only.`
          : "My Watchlist membership is account-synced. Waiting for authenticated Alpaca snapshot data where available."}
      </p>
    </div>
  );
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatOptionalCurrency(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : formatCurrency(number);
}

function formatSignedPercent(value) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function formatOptionalPercent(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : formatSignedPercent(number);
}

function formatTimestamp(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "time unavailable";
  return parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default NinaMissionPanel;
